/**
 * BuildProposalDialog — S29. One-click Stage 3 proposal from a reviewed
 * commercial quote. Pre-populates every field from the quote record; the
 * Account Manager edits and sends directly. Logged on the quote record.
 */
import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { apiFetch } from '../../lib/apiFetch';
import { toast } from 'sonner';
import { FileText, Send, Save } from 'lucide-react';

const GOLD = '#C9A962';
const FREQ_LABELS = {
  '3x_week': '3x per week', '5x_week': '5x per week', daily: 'Daily',
  '7x_week': '7 days per week', multiple_daily: 'Multiple times daily',
};

function buildDraft(quote) {
  const fp = quote?.facility_profile || {};
  const rng = quote?.estimate_range;
  const ib = quote?.internal_breakdown;
  const freq = FREQ_LABELS[fp.cleaning_frequency] || fp.cleaning_frequency || '';
  const recPrice = ib?.recommended_contract_price;
  const scopeParts = [
    `Recurring commercial cleaning program for ${quote?.property_name || 'the facility'} (${fp.business_type || quote?.package_name || 'commercial'}), approximately ${Number(fp.square_footage || 0).toLocaleString()} sqft across ${fp.buildings || 1} building(s) and ${fp.floors || 1} floor(s).`,
    `Service frequency: ${freq}.`,
    fp.restroom_count ? `Restroom care for ${fp.restroom_count} restroom(s).` : '',
    (fp.hard_floor_sqft || fp.carpet_sqft) ? `Floor care covering ${Number(fp.hard_floor_sqft || 0).toLocaleString()} sqft hard floor and ${Number(fp.carpet_sqft || 0).toLocaleString()} sqft carpet.` : '',
    (fp.additional_services || []).length ? `Add-on services: ${fp.additional_services.join(', ')}.` : '',
  ].filter(Boolean);
  const pkg = ['essential', 'professional', 'executive'].includes(quote?.package_key)
    ? quote.package_key : 'professional';
  return {
    submission_id: quote?.submission_id,
    client_name: quote?.full_name || '',
    facility_name: quote?.property_name || '',
    business_type: fp.business_type || 'office',
    scope: scopeParts.join(' '),
    selected_package: pkg,
    service_frequency: freq,
    specialty_services: (fp.additional_services || []).join(', '),
    estimated_low: rng?.low ?? (recPrice ? Math.round(recPrice) : 0),
    estimated_high: rng?.high ?? (recPrice ? Math.round(recPrice) : 0),
    contract_term_months: 12,
    exclusions: '',
    account_contact: '',
    client_email: quote?.email || '',
  };
}

const inputCls = 'w-full rounded bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-white text-sm';
const labelCls = 'text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block';

export default function BuildProposalDialog({ quote, open, onClose, onDone }) {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (quote && open) setF(buildDraft(quote)); }, [quote, open]);

  if (!f) return null;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async (sendAfter) => {
    if (!f.client_name || !f.facility_name || !f.scope) { toast.error('Client, facility and scope are required'); return; }
    setBusy(true);
    try {
      const payload = {
        submission_id: f.submission_id,
        client_name: f.client_name,
        facility_name: f.facility_name,
        business_type: f.business_type,
        scope: f.scope,
        selected_package: f.selected_package,
        service_frequency: f.service_frequency,
        specialty_services: f.specialty_services.split(',').map((s) => s.trim()).filter(Boolean),
        sustainability_options: [],
        estimated_low: Number(f.estimated_low) || 0,
        estimated_high: Number(f.estimated_high) || 0,
        contract_term_months: Number(f.contract_term_months) || 12,
        exclusions: f.exclusions || null,
        account_contact: f.account_contact || null,
        client_email: f.client_email || null,
      };
      const res = await apiFetch('/api/commercial-quote/proposals', { method: 'POST', body: payload });
      if (sendAfter) {
        await apiFetch(`/api/commercial-quote/proposals/${res.proposal_id}/email`, { method: 'POST' });
        toast.success(`Proposal sent to ${f.client_email}`);
      } else {
        toast.success('Proposal draft saved');
      }
      onDone?.(res.proposal_id);
      onClose();
    } catch (e) {
      toast.error(e.detail || 'Proposal failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-zinc-900 border border-[#C9A962]/50 text-white" data-testid="build-proposal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: GOLD, fontFamily: 'Barlow Condensed, sans-serif' }}>
            <FileText className="h-5 w-5" /> STAGE 3 PROPOSAL — {f.facility_name?.toUpperCase()}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className={labelCls}>Client Name</label>
            <input className={inputCls} value={f.client_name} onChange={set('client_name')} data-testid="bp-client-name" /></div>
          <div><label className={labelCls}>Facility Name</label>
            <input className={inputCls} value={f.facility_name} onChange={set('facility_name')} data-testid="bp-facility-name" /></div>
          <div><label className={labelCls}>Business Type</label>
            <input className={inputCls} value={f.business_type} onChange={set('business_type')} data-testid="bp-business-type" /></div>
          <div><label className={labelCls}>Client Email</label>
            <input className={inputCls} value={f.client_email} onChange={set('client_email')} data-testid="bp-client-email" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Scope of Work</label>
            <textarea className={`${inputCls} min-h-[90px]`} value={f.scope} onChange={set('scope')} data-testid="bp-scope" /></div>
          <div><label className={labelCls}>Recommended Package</label>
            <select className={inputCls} value={f.selected_package} onChange={set('selected_package')} data-testid="bp-package">
              <option value="essential">Essential Care</option>
              <option value="professional">Professional Care</option>
              <option value="executive">Executive Care</option>
            </select></div>
          <div><label className={labelCls}>Service Frequency</label>
            <input className={inputCls} value={f.service_frequency} onChange={set('service_frequency')} data-testid="bp-frequency" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Add-Ons / Specialty Services (comma separated)</label>
            <input className={inputCls} value={f.specialty_services} onChange={set('specialty_services')} data-testid="bp-addons" /></div>
          <div><label className={labelCls}>Monthly Investment — Low ($)</label>
            <input type="number" min="0" className={inputCls} value={f.estimated_low} onChange={set('estimated_low')} data-testid="bp-est-low" /></div>
          <div><label className={labelCls}>Monthly Investment — High ($)</label>
            <input type="number" min="0" className={inputCls} value={f.estimated_high} onChange={set('estimated_high')} data-testid="bp-est-high" /></div>
          <div><label className={labelCls}>Contract Term (months)</label>
            <select className={inputCls} value={f.contract_term_months} onChange={set('contract_term_months')} data-testid="bp-term">
              {[12, 18, 24, 30, 36, 48, 60].map((m) => <option key={m} value={m}>{m} months</option>)}
            </select></div>
          <div><label className={labelCls}>Account Contact</label>
            <input className={inputCls} placeholder="NECS Account Management" value={f.account_contact} onChange={set('account_contact')} data-testid="bp-account-contact" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Exclusions</label>
            <input className={inputCls} placeholder="Per executed service agreement" value={f.exclusions} onChange={set('exclusions')} data-testid="bp-exclusions" /></div>
        </div>
        <p className="text-[10px] text-zinc-500">
          Service schedule, standards, terms and the acceptance section are included automatically in the branded proposal. The proposal will be logged against this quote with timestamp and sender.
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={() => save(false)} disabled={busy}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border disabled:opacity-50"
            style={{ borderColor: GOLD, color: GOLD }} data-testid="bp-save-draft-btn">
            <Save className="h-3.5 w-3.5" /> Save Draft
          </button>
          <button onClick={() => save(true)} disabled={busy || !f.client_email}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50"
            style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="bp-save-send-btn"
            title={!f.client_email ? 'Client email required to send' : 'Save and email the proposal now'}>
            <Send className="h-3.5 w-3.5" /> Save &amp; Send Proposal
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
